/**
 * 대장간 — 제작 · 강화 · 머지 · 승급 · 분해.
 *
 * 사다리 한 층의 전체 그림이 이 화면 하나에 있어야 한다:
 *   강화석 → +0 →(은화)→ 안전수치 →(머지 또는 강화석)→ +10 →(승급)→ 다음 층 +0
 *
 * ── 개편 (사용자: *"인벤토리 및 강화, 상점 UI 개편"*) ─────────────
 * **줄 세우기를 그만뒀다.** 전에는 장비 한 자루가 한 줄이고 그 줄마다 버튼이
 * 셋이었다. 열네 자루면 **버튼 마흔두 개가 세로로 늘어서고 2.7화면**이 된다.
 * 사용자 표현이 정확했다 — *"선택하고도 한참 밑을 내려야 합성 버튼이 보이는
 * 조악스러운 UI"*. 줄마다 버튼을 붙이면 **고른다는 개념 자체가 없다.**
 *
 * 지금은 **하나를 고르고, 그 하나에 대해서만 행동한다:**
 *   가운데(스크롤) — 장비 격자 · 제작 · 접힌 설명
 *   아래(안 스크롤) — 고른 자루의 이름·수치·버튼
 *
 * ⚠ 아래를 **덮개로 얹지 않았다.** 예전 `.actbar` 는 바닥에 붙은 sticky 라
 * 목록 위를 덮어 **클릭을 삼켰다.** `split()` 은 상자를 둘로 갈라 구조적으로
 * 안 겹치게 한다(`common.js`).
 */
import {
  BSTONE_KEY, MAX_GRADE, MAX_PLUS, POTIONS, SAFE_PLUS, SHARD_KEY, ULTIMATE_CHANCE, ULTIMATE_BSTONES,
  blessedStoneCost, enhanceChance, enhanceAttemptChance, enhanceStones, gradeOf, investedSilver, isUltimateEnhance, safeCost, safePlusOf,
} from '../../world/map.js';
import { S } from '../../core/state.js';
import { whyNot } from '../../world/places.js';
import {
  ascendRule, buyBtn, cell, eqName, eqTitle, gearGlyph, gearTone, guildRingInfo, templeRingInfo, gradeCol, junkName, lore, muted, npcHead, num, purse, split, stock,
} from './common.js';
import { JUNK_BAND_KEYS, breakdownBlock, breakdownYield, canAscend, sameLineage } from '../../world/gearnames.js';
import { enhancementEffects } from '../equipment-compare.js';

/** Server-derived modifiers and the existing shared chance rule. Old payloads say explicitly that only base odds are known. */
export function forgeChanceOf(e, y) {
  const ultimate = isUltimateEnhance(e.plus, e.grade || 0);
  const actual = ultimate || Number.isFinite(y.forgeLuck) && Number.isFinite(y.forgeBoost);
  const chance = actual ? enhanceAttemptChance(e.plus, e.grade || 0, y.forgeLuck || 0, y.forgeBoost || 1) : enhanceChance(e.plus, e.grade || 0);
  return { chance, actual, text: `${(chance * 100).toLocaleString('ko-KR', { maximumFractionDigits: 2 })}%${actual ? '' : ' 기본'}` };
}

export function enhancementChoices(e, y) {
  const grade = e.crude ? 0 : e.grade || 0, safe = e.crude ? SAFE_PLUS : safePlusOf(grade);
  const ultimate = !e.crude && isUltimateEnhance(e.plus, grade);
  if (e.plus >= MAX_PLUS && !ultimate || e.crude && e.plus >= safe) return '';
  const odds = forgeChanceOf(e, y);
  const section = (title, cost, chance, min, max, failure) => `<h4>${title} · ${chance}</h4>`
    + `<div class="kv"><span>1회 소모</span><b>${cost}</b></div>`
    + `<div class="kv"><span>성공한 강화 수치</span><b>+${min}${max === min ? '' : `~+${max}`}</b></div>`
    + enhancementEffects(e, y, min, max) + muted(failure);
  let body = '';
  if (!ultimate) body += section(e.plus < safe ? '은화 강화' : '강화석 강화',
    e.plus < safe ? `${num(safeCost(e.plus, grade))} 은화` : `강화석 ${num(enhanceStones(e.plus, grade))}개`,
    e.plus < safe ? '100%' : odds.text, e.plus + 1, e.plus + 1,
    e.plus < safe ? '현재 단계에서 한 단계 오른다.' : '실패하면 강화석만 소모하고 장비·강화 수치는 유지된다.');
  if (!e.crude) body += section('축복 강화', `축복받은 강화석 ${num(blessedStoneCost(e.plus, grade))}개`, odds.text,
    e.plus + 1, ultimate ? e.plus + 3 : Math.min(MAX_PLUS, e.plus + 3),
    '실패하면 축복받은 강화석만 소모하고 장비·강화 수치는 유지된다.');
  if (!odds.actual && e.plus >= safe) body += muted('행운·활성 부스트를 반영한 확률은 마을에서 최신 상태를 받은 뒤 확인할 수 있다.');
  const min = e.plus + 1, max = e.crude ? min : ultimate ? e.plus + 3 : Math.min(MAX_PLUS, e.plus + 3);
  return `<div class="kv"><span>성공 시</span><b>${ultimate ? '' : `일반 +${min}`}${!e.crude ? `${ultimate ? '' : ' · '}축복 +${min}${max === min ? '' : `~+${max}`}` : ''}</b></div>`
    + enhancementEffects(e, y, min, max)
    + `<details class="lore" id="forge-investment"><summary>강화 방식별 비용·결과 비교</summary><div class="in">${body}</div></details>`;
}

/**
 * **분해 계획 — 단 한 자리**(14.493). 대장간의 단건 「분해」 버튼, 「모두 분해 (N개)」의 N, 확인 창의
 * 예상 재료가 전부 여기서 나온다. 판정은 서버 `breakdownBlock` 의 미러, 재료는 서버 `breakdownYield` 의
 * 미러다(`gearnames.js` · `check-mirror` 가 서버 답과 대조). 화면이 따로 세면 확인 창이 말한 개수와
 * 서버가 실제로 부수는 개수가 갈린다.
 *   · `listed` — 목록에 보이는 것: 벗기만 하면 분해되는 것까지(빌림·귀속·특별품·+1 이상은 아예 빠진다)
 *   · `ready`  — 지금 분해되는 것: 「모두 분해」가 보내면 서버가 같은 판정으로 고르는 것
 *   · `materials` — 잡템별 예상 수량 범위 합, `shardPct`·`shardItems` — 강화석 회수 확률과 그 대상 수
 */
export function breakdownPlan(y) {
  const eq = y?.inv?.equips || [];
  const worn = y?.inv?.equipped || {};
  const listed = eq.filter((e) => breakdownBlock(e, {}) === null);
  const ready = listed.filter((e) => breakdownBlock(e, worn) === null);
  const byJunk = new Map();
  let shardPct = 0, shardItems = 0;
  for (const e of ready) {
    const out = breakdownYield(e.grade ?? 0, e.crude);
    const sum = byJunk.get(out.junk) || { min: 0, max: 0 };
    sum.min += out.min; sum.max += out.max;
    byJunk.set(out.junk, sum);
    if (out.shard > 0) { shardItems++; shardPct = Math.round(out.shard * 100); }
  }
  // 잡템 띠 순서(싼 것부터)로 — 서버 결과 문장과 같은 순서다
  const materials = [...byJunk].sort(([a], [b]) => JUNK_BAND_KEYS.indexOf(a) - JUNK_BAND_KEYS.indexOf(b))
    .map(([key, sum]) => ({ key, name: junkName(key), ...sum }));
  return { listed, ready, materials, shardPct, shardItems };
}

/**
 * 「모두 분해」 확인 창 문장 — 개수·예상 재료(잡템마다 한 줄)·남는 것. `breakdownPlan` 의 결과만 읽는다.
 * 재료를 한 줄에 이어 쓰면 폰 폭에서 「뿔 / 4~8개」처럼 이름과 수량이 갈라져 읽힌다 — 줄마다 하나씩 둔다.
 */
export function breakdownAllQuestion(plan) {
  const n = plan.ready.length;
  const mats = plan.materials.map((m) => `· ${m.name} ${num(m.min)}~${num(m.max)}개`).join('\n');
  const keptWorn = plan.listed.length - n;
  return `+0 장비 ${num(n)}개를 한꺼번에 분해한다.\n\n`
    + `예상 재료\n${mats}`
    + (plan.shardItems ? `\n· 강화석 — 조잡하지 않은 ${num(plan.shardItems)}개가 각각 ${plan.shardPct}% 확률로 1개` : '')
    + `\n\n착용 중${keptWorn ? ` ${num(keptWorn)}개` : ''}·강화한 장비·귀속·신전 반지는 남는다. 되돌릴 수 없다. 분해할까?`;
}

export function render({ y, at, eq, j }) {
  // 가방 + 창고. 서버가 둘 다 쓰므로 화면도 둘 다 세야 버튼이 거짓말을 안 한다.
  const stones = stock(y, SHARD_KEY);
  const bstones = stock(y, BSTONE_KEY);
  const bagS = j[SHARD_KEY] || 0;
  const ctx = { y, at, eq, stones, bstones, bagS };

  // ── 아래: 살피는 곳 ────────────────────────────────────────────
  let list = npcHead();

  list += `<div class="sec"><h3>내 장비 <em>${eq.length}개</em></h3>`;
  list += eq.length
    ? '<div class="grid">' + eq.map((e) => cell({
      key: 'f:' + e.id,
      ...gearTone(e), // F19 — 칸 색·테두리 등급색·영웅부터 빛(rare)
      glyph: gearGlyph(e.slot, e.crude ? null : e.grade || 0),
      badge: '+' + e.plus,
      // 묶음 AB — 입고 있는 장비에 「착용」 표식(가방과 같은 칸 규칙). 강화 격자에서 어느 것이 지금 몸에 있는지 안 보였다
      on: Object.values(y.inv?.equipped || {}).includes(e.id),
      sel: S.forgeSel.includes(e.id),
      title: eqTitle(e),
    })).join('') + '</div>'
    : muted('강화할 장비가 없다. 아래에서 <b>강화석</b>으로 무기나 갑옷을 제작해 달라.');
  list += '</div>';

  // 제작 — 한 줄. 장비가 없을 때만 위로 올라오지 않는다(격자가 먼저다)
  const split2 = (all, bag) => (all > bag ? ` <span class="qt">가방 ${bag} · 창고 ${all - bag}</span>` : '');
  list += `<div class="item"><span class="nm">장비 제작 <span class="qt">강화석 1개 → 하급 +0 · ${num(stones)}개 보유</span>${split2(stones, bagS)}</span>
    ${buyBtn('무기 제작', "act.shape('weapon')", { at, need: 'shape', more: stones ? [] : ['강화석 없음'] })}
    ${buyBtn('갑옷 제작', "act.shape('armor')", { at, need: 'shape', more: stones ? [] : ['강화석 없음'] })}</div>`;

  list += lore('장비 제작', `
    <b>강화석</b> 하나를 하급 +0 장비로 만든다. 낮은 확률로 <span class="bless">축복</span>이 붙는다.<br>
    상위 등급 장비는 <b>승급</b>하거나 몬스터를 처치해 얻는다.`);
  list += lore('강화 방법', `
    ① <b>안전 구간</b>(+0 ~ 안전수치) — 은화만. 100% 확정.<br>
    ② <b>머지</b> — 같은 부위·등급·<b>강화·축복</b>까지 같은 장비 두 개를 합친다. 100% 확정.<br>
    ③ <b>강화석</b> — 확률. <b>실패해도 장비는 파괴되지 않는다.</b> 강화석만 사라진다.<br>
    ④ <b>축복받은 강화석</b>(보유 ${bstones}) — 강화석과 <b>같은 확률</b>, 성공하면 <b>1~3 단계</b>. 구역 보스만 떨군다.<br>
    등급별 강화 상한은 <b>+${MAX_PLUS}</b>이다. <b>승급</b> — ${ascendRule()}<br>
    <b>일반 드롭 장비 6부위</b>(무기·갑옷·투구·장갑·신발·망토)는 강화·승급할 수 있다.
    가방은 강화할 수 없다. <b>반지</b>도 강화·승급할 수 있다. 같은 반지 두 개를 +${MAX_PLUS}에서 합치면 궁극이 된다.<br>
    마지막 <b>궁극</b>만 +${MAX_PLUS} 이후 <b>축복받은 강화석 ${ULTIMATE_BSTONES}개 · ${ULTIMATE_CHANCE * 100}%</b>로
    제한 없이 오른다(연금술사).`);

  /**
   * 분해 — 단건 목록은 참고용이라 접어 두고, **「모두 분해 (N개)」는 접지 않은 줄**에 둔다(14.493 · 사용자
   * *"대장간에서 분해할때 한꺼번에 다 분해할 수 있도록"*). N·목록·단건 버튼이 전부 `breakdownPlan` 한 자리에서
   * 나온다 — 착용 중인 것은 대상이 아니다(맨몸 사고 방지 · 목록에는 「벗은 후 분해 가능」으로 남는다).
   */
  const plan = breakdownPlan(y);
  const ready = new Set(plan.ready.map((e) => e.id));
  if (eq.length) {
    const n = plan.ready.length;
    const none = plan.listed.length ? '착용 중인 것만 남음 — 벗은 후 분해 가능' : '분해할 +0 장비 없음';
    list += `<div class="item bulkbreak"><span class="nm">분해 <span class="qt">${n
      ? `+0 장비 ${num(n)}개를 한 번에 · 착용 중·강화·귀속은 남긴다` : '+0 장비만 분해된다'}</span></span>
      ${buyBtn(`모두 분해 (${num(n)}개)`, 'act.breakdownAll()', { at, need: 'breakdownAll', more: n ? [] : [none] })}</div>`;
  }
  if (plan.listed.length) {
    let d = muted(`+0 장비를 소모해 <b>잡템</b>을 얻는다. 조잡한 장비를 제외하면 낮은 확률로 <b>강화석 1개</b>도 나온다.`);
    for (const e of plan.listed) {
      d += `<div class="item"><span class="nm">${eqName(e)}</span>
        ${ready.has(e.id)
          ? buyBtn('분해', `act.breakdown('${e.id}')`, { at, need: 'breakdown' })
          : `<span class="qt">착용 중 · 벗은 후 분해 가능</span>`}</div>`;
    }
    list += `<details class="lore"><summary>분해 <span class="qt">${plan.listed.length}개</span></summary>`
      + `<div class="in">${d}</div></details>`;
  }
  list += lore('등급별 강화 안내', ladder());

  const action = focus(ctx);
  const picked = S.forgeSel.length > 0 || !!S.autoEnhance;
  /**
   * `EZ` — 고른 장비의 행동은 엄지 옆 **마지막 형제 띠**로 내린다.
   * 아무것도 안 골랐을 때의 안내만 위에 남기고, 실제 버튼·진행·결과는 전부
   * `split` 셋째 칸을 쓴다. `position` 덮개가 아니라 목록과 높이를 나누는 형제라
   * 아래 칸을 덮거나 클릭을 삼키지 않는다.
   */
  return split(picked ? '' : action, list, picked ? action : '');
}

/**
 * 고른 자루에 대해 **할 수 있는 것만** 보여준다.
 *
 * 하나도 안 골랐으면 자리는 지키되 조용히 둔다 — 빈 덩어리가 화면을 먹으면
 * 그것도 손해지만, 자리가 아예 없다가 생기면 **격자가 밀려 손이 헛간다.**
 */
function focus({ y, at, eq, stones, bstones, bagS }) {
  const ids = S.forgeSel;
  const [a, b] = ids.map((id) => eq.find((q) => q.id === id));
  if (!a) {
    if (S.autoEnhance?.active) return autoProgress(S.autoEnhance, eq, stones);
    if (S.autoEnhance) return autoDone(S.autoEnhance, eq);
    return `<div class="focus idle">강화할 장비를 선택해 달라. 두 개를 선택하면 <b>합치기·승급</b> 조건을 확인할 수 있다.</div>`;
  }
  const cancel = `<button class="ghost" onclick="act.clearForge()">해제</button>`;
  const done = S.autoEnhance && !S.autoEnhance.active && S.autoEnhance.id === a.id
    ? autoDone(S.autoEnhance, eq) : '';

  // 자동 작업 중에는 같은 장비의 수동 버튼을 함께 두지 않는다. 서버는 둘을 안전하게
  // 줄 세울 수 있지만, 사용자가 어느 동작이 재료를 썼는지 알 수 없게 된다.
  if (S.autoEnhance?.active) return autoProgress(S.autoEnhance, eq, stones, cancel);

  // 두 자루 — 머지 또는 승급
  if (b) return done + pair(a, b, cancel);

  // 한 자루 — 그 자루가 지금 갈 수 있는 길
  const grade = a.crude ? 0 : (a.grade || 0);
  const safe = a.crude ? SAFE_PLUS : safePlusOf(grade);
  const top = a.plus >= MAX_PLUS;
  let row = '';
  let note = '';
  let auto = '';

  if (top && !a.crude && grade < MAX_GRADE) {
    note = `강화 상한에 도달했다. <b>같은 부위·${gradeOf(grade).name} +${MAX_PLUS}</b> 장비를 하나 더 선택하면 `
      + `<b>승급</b>한다 — <span class="qt">재료도 +${MAX_PLUS} 이라야 한다.</span>`;
  } else if (top && !a.crude && isUltimateEnhance(a.plus, grade)) {
    /**
     * ⛔ **한때 이 자리가 라이브에서 죽어 있었다**(2026-09-06). UNI-ALCHEMY 로 서버
     *   `enhance()` 가 궁극을 닫고 축복석 경로로 보냈는데, **이 버튼만 그대로 `act.enhance`
     *   를 부르고 「강화석 10 · 1%」라고 적혀 있었다.** 눌러도 거절 문구만 돌아왔고,
     *   보내라는 연금술사는 아직 화면에 없었다 — **되던 기능이 사라진 것이다.**
     *
     *   서버가 재료를 바꾸면 **그 재료를 세고 그 액션을 부르는 자리**가 같이 바뀌어야 한다.
     *   `check-mirror` 는 이걸 못 잡는다 — 서버와 화면이 **똑같이 낡으면** 통과한다.
     */
    const need = blessedStoneCost(a.plus, grade);
    const lack = [];
    const whyB = whyNot(at, 'bless');
    if (whyB) lack.push(whyB);
    if (bstones < need) lack.push(`축복받은 강화석 ${need - bstones} 부족`);
    const dis = lack.length ? 'disabled' : '';
    row = `<button class="gold" ${dis} onclick="act.bless('${a.id}')">축복받은 강화석 ${need} · ${ULTIMATE_CHANCE * 100}%</button>`
      + (lack.length ? `<span class="qt lack">${lack.join(' · ')}</span>` : '');
    note = '<b>강화 상한 없음.</b> 실패해도 장비는 안 부서진다. 성공하면 <b>1~3단계</b> 오른다. '
      + '행운·광고 배수도 1% 규칙을 바꾸지 않는다.';
    const goal = a.plus < 15 ? 15 : a.plus + 5;
    auto = autoButtons(a, at, goal, true, bstones, purse(y));
  } else if (top) {
    note = '조잡한 장비는 더 강화할 수 없다.';
  } else if (a.plus < safe) {
    // 안전 구간은 **은화**로 확정 강화된다 — 비용을 누르기 전에 보여준다
    const cost = safeCost(a.plus, grade);
    const rest = investedSilver(safe, safe, grade) - investedSilver(a.plus, safe, grade);
    const lack = [];
    const whyF = whyNot(at, 'forge');
    if (whyF) lack.push(whyF);
    if (purse(y) < cost) lack.push(`은화 ${num(cost - purse(y))} 부족`);
    const dis = lack.length ? 'disabled' : '';
    row = `<button class="gold" ${dis} onclick="act.forge1('${a.id}')">+${a.plus + 1} 강화 · ${num(cost)} 은화</button>
      <button ${dis} onclick="act.forgeAll('${a.id}')">+${safe}까지 · ${num(rest)} 은화</button>`
      + (lack.length ? `<span class="qt lack">${lack.join(' · ')}</span>`
        : y.silver < cost ? '<span class="qt">창고에서 나간다</span>' : '');
    note = `<b>+${safe}</b> 까지는 은화로 <b>100% 확정</b>이다.`;
    if (!a.crude) auto = autoButtons(a, at, MAX_PLUS, false, stones, purse(y));
  } else if (a.crude) {
    note = '조잡한 장비는 안전수치까지만 강화할 수 있다. 상위 등급 장비로 교체해 달라.';
  } else {
    const need = enhanceStones(a.plus, grade);
    const pct = forgeChanceOf(a, y).text;
    const lack = [];
    const whyE = whyNot(at, 'enhance');
    if (whyE) lack.push(whyE);
    if (stones < need) lack.push(`강화석 ${need - stones} 부족`);
    const dis = lack.length ? 'disabled' : '';
    const blessedNeed = blessedStoneCost(a.plus, grade);
    row = `<button class="gold" ${dis} onclick="act.enhance('${a.id}')">강화석 ${need} · ${pct}</button>`
      + (bstones > 0 ? buyBtn(`축복받은 강화석 ${blessedNeed} · ${pct}`, `act.bless('${a.id}')`, { at, need: 'bless', more: bstones < blessedNeed ? [`축복받은 강화석 ${blessedNeed - bstones} 부족`] : [] }) : '')
      + (lack.length ? `<span class="qt lack">${lack.join(' · ')}</span>`
        : bagS < need ? `<span class="qt">창고에서 ${need - bagS}개가 나간다</span>` : '');
    note = '<b>실패해도 장비는 안 부서진다.</b> 강화석만 사라진다.';
    auto = autoButtons(a, at, MAX_PLUS, false, stones, purse(y));
  }

  // 머지는 어느 상태에서든 고를 수 있다 — 짝을 고르라는 안내를 같이 둔다
  const mergeBlocked = a.crude || top;
  return done + `<div class="fname"><b>${eqName(a)}</b>
      <span class="qt">${a.crude ? '조잡 · 머지 불가'
        : grade >= MAX_GRADE ? `안전 +${safe} · +${MAX_PLUS} 이후 강화 상한 없음`
        : `안전 +${safe} · 상한 +${MAX_PLUS}`}</span></div>
    <div class="fnote">${note}</div>
    <div class="ux-actions">${row}${cancel}</div>
    ${enhancementChoices(a, y)}
    ${guildRingInfo(a, y)}${templeRingInfo(a, y)}
    ${auto}
    ${mergeBlocked ? '' : '<div class="fnote">이 장비를 남기려면, 부위·등급·강화·축복이 같은 재료를 하나 더 고른다.</div>'}`
    + `<div class="row"><button class="ghost" onclick="act.gearWork('${a.id}','inv')">가방에서 비교·착용</button>`
    + `<button class="ghost" onclick="act.gearWork('${a.id}','alchemy')">이 장비 재추첨</button></div>`;
}

/**
 * 자동은 클라이언트 반복 요청이 아니다. 시작 패킷 하나만 보내고 서버가 단계마다
 * 계정 FIFO를 다시 탄다. 일반 등급은 +10, 궁극은 짧은 목표와 재료 소진 중 고른다.
 */
function autoButtons(a, at, target, exhaust, stones, silver) {
  const lack = [];
  const why = whyNot(at, 'autoEnhance');
  if (why) lack.push(why);
  const grade = a.grade || 0;
  const safe = safePlusOf(grade);
  if (a.plus < safe) {
    // 서버도 같은 `pot_s` 표에서 다섯 개 값을 도출한다. 100을 양쪽에 손으로 적지 않는다.
    const reserve = (POTIONS.find((p) => p.id === 'pot_s')?.price || 20) * 5;
    const cost = safeCost(a.plus, grade);
    if (silver - cost < reserve) lack.push(`물약값을 남기면 은화 ${num(cost + reserve - silver)} 부족`);
  } else if (isUltimateEnhance(a.plus, grade)) {
    // 궁극 구간은 **축복석**을 태운다(서버 `autoEnhanceStep` 이 재료를 바꾼다).
    //   여기서 강화석을 세면 부르는 쪽이 축복석을 넘겨도 「강화석 부족」이라 거짓말한다.
    const need = blessedStoneCost(a.plus, grade);
    if (stones < need) lack.push(`축복받은 강화석 ${need - stones} 부족`);
  } else {
    const need = enhanceStones(a.plus, grade);
    if (stones < need) lack.push(`강화석 ${need - stones} 부족`);
  }
  const dis = lack.length ? 'disabled' : '';
  return `<details class="lore" id="forge-repeat"><summary>여러 번 강화하기 · 목표와 중지</summary><div class="in"><div class="row auto-row">
    <button class="auto" ${dis} onclick="act.autoEnhance('${a.id}',${target})">+${target} 자동</button>
    ${exhaust ? `<button class="ghost" ${dis} onclick="act.autoEnhanceAll('${a.id}')">재료 소진까지</button>` : ''}
    ${lack.length ? `<span class="qt lack">${lack.join(' · ')}</span>` : '<span class="qt">수동과 같은 확률·비용 · 물약 5개 값은 남긴다</span>'}
  </div><div class="fnote">시도할 때마다 재료가 소모된다. 진행 중 누적 소모를 확인하고 멈출 수 있다.</div></div></details>`;
}

/** 선택을 바꿔도 진행 중인 장비와 결과가 사라지지 않는 고정 진행줄. */
function autoProgress(job, eq, stones, cancel = '') {
  const e = eq.find((q) => q.id === job.id);
  const plus = e?.plus ?? job.startPlus;
  const goal = job.target === null ? '재료 소진' : `+${job.target}`;
  const span = job.target === null ? 0 : Math.max(1, job.target - job.startPlus);
  const pct = span ? Math.max(0, Math.min(100, Math.round((plus - job.startPlus) / span * 100))) : 0;
  return `<div class="fname"><b>${job.name} +${plus}</b>
      <span class="qt">목표 ${goal} · ${job.stop ? '중지 중' : '자동 강화 중'}</span></div>
    ${job.target === null ? '' : `<div class="autometer"><i style="width:${pct}%"></i></div>`}
    <div class="kv"><span>시도 / 성공</span><b>${num(job.attempts)}회 / ${num(job.successes)}회</b></div>
    <div class="kv"><span>사용 / 남은 재료</span><b>은화 ${num(job.spentSilver)} · 강화석 ${num(job.spentStones)}${job.spentBlessed ? ` · 축복받은 강화석 ${num(job.spentBlessed)}` : ''} / ${num(stones)}개</b></div>
    <div class="row"><button class="bad" ${job.stop ? 'disabled' : ''} onclick="act.autoEnhanceStop()">${job.stop ? '멈추는 중…' : '자동 강화 멈춤'}</button>${cancel}</div>
    <div class="fnote">수동 강화와 확률·비용이 같다. 목표에 도달하거나 재료가 부족하면 멈춘다. 물약 5개를 살 은화는 남긴다.</div>`;
}

function autoDone(job, eq) {
  const e = eq.find((q) => q.id === job.id);
  const ended = e ? `+${num(e.plus)}` : '장비 없음';
  return `<div class="auto-result"><b>자동 강화 결과 · ${job.reason || '종료'}</b><br>
    ${num(job.attempts)}회 시도 · ${num(job.successes)}회 성공 · 은화 ${num(job.spentSilver)} · 강화석 ${num(job.spentStones)}${job.spentBlessed ? ` · 축복받은 강화석 ${num(job.spentBlessed)}` : ''} · ${ended}
    <button class="ghost" onclick="act.autoEnhanceDismiss()">확인</button></div>`;
}

/**
 * 두 자루 — **서버 조건을 그대로 거울로 삼는다** (`town.ts` 의 `forge` ② 와 `ascend`).
 *
 * 둘은 조건이 다르다 — 처음엔 하나로 묶어 적었다가 두 군데가 갈라졌다:
 *   · 머지는 **축복까지 같아야** 한다. 승급은 축복을 안 본다.
 *   · 승급은 **궁극에서 막힌다**(더 오를 층이 없다). 궁극 +10 이후는 강화석만 쓴다.
 * 화면이 서버보다 너그러우면 눌렀는데 거절당하고, 인색하면 되는 걸 못 누른다.
 * 둘 다 사용자에게는 고장으로 보인다.
 */
function pair(a, b, cancel) {
  const safe = a.crude ? SAFE_PLUS : safePlusOf(a.grade || 0);
  if (a.plus < safe) return `<div class="fname"><b>${eqName(a)}</b></div>`
    + `<div class="fnote">지금은 안전 구간이다. +${a.plus + 1} 강화에는 <b>${num(safeCost(a.plus, a.crude ? 0 : a.grade || 0))} 은화</b>만 들고, 두 번째 장비 <b>${eqName(b)}</b>는 소모하지 않는다.</div>`
    + `<div class="row"><button onclick="act.gearWork('${a.id}','forge')">두 번째 선택 해제 · 안전 강화 보기</button>${cancel}</div>`;
  /**
   * UNI-Q36 — 반지도 오른다. 귀속·특별품은 **같은 것끼리만** 재료가 된다(서버 `sameLineage` 의 미러).
   * 귀속 반지 둘(같은 주인·같은 상품)은 되고, 귀속 무기는 여전히 재료가 못 된다.
   */
  if (!sameLineage(a, b)) return `<div class="fname"><b>${eqName(a)}</b> + <b>${eqName(b)}</b></div>`
    + `<div class="row"><span class="qt lack">${a.guildBound != null || b.guildBound != null
      ? '귀속 장비는 같은 주인의 같은 반지끼리만 합친다.' : '신전 반지는 신전 반지끼리만 합친다.'}</span>${cancel}</div>`;
  const gA = a.grade || 0, gB = b.grade || 0;
  /**
   * `+10`에 닿은 **첫째 장비**는 승급만 시도한다. 궁극은 승급 대신 강화석만 쓴다.
   * 일반 머지로 되돌리면 궁극 +10 두 자루에 「+11」 버튼이 생긴다 — 서버는 거절한다.
   *
   * ⚠⚠ **승급 재료도 `+10` 이라야 한다 (`MERGE-R4`).** 여기 *"EX 규칙대로 재료의
   *   plus 를 안 본다"* 가 남아 있었다 — 서버(`canMerge`)는 이미 거절하는데 화면만
   *   +0 재료에 승급 버튼을 켰다. **눌렀는데 거절당하는 버튼**은 바로 위 머리말이
   *   금지한 그 고장이다. 규칙이 뒤집히면 화면의 짝 판정도 같이 뒤집는다.
   */
  const ultimate = !a.crude && isUltimateEnhance(a.plus, gA);
  const wantsAscend = !a.crude && a.plus >= MAX_PLUS && gA < MAX_GRADE;
  const asc = wantsAscend && canAscend(a.plus, gA, !!a.crude, a.slot);
  // 같은 자루를 두 번 고른 것은 짝이 아니다 — 서버 `canMerge` 의 「자기 자신 금지」 미러
  const same = a.id !== b.id && a.slot === b.slot && gA === gB && !a.crude && !b.crude;
  const ok = ultimate ? false : wantsAscend
    ? asc && same && b.plus >= MAX_PLUS
    : same && a.plus === b.plus && a.blessed === b.blessed;

  const why = ultimate ? `궁극 +${MAX_PLUS} 이후는 축복받은 강화석 ${ULTIMATE_BSTONES}개·${ULTIMATE_CHANCE * 100}%로만 오른다`
    : a.id === b.id ? '같은 것을 두 번 골랐다'
    : a.crude || b.crude ? '조잡한 장비는 합쳐지지 않는다'
    : a.slot !== b.slot ? '부위가 다르다'
    : gA !== gB ? '등급이 다르다'
    : wantsAscend && !asc ? `${gradeOf(gA).name}${gA >= MAX_GRADE ? '가 상한이다. 더 오를 곳이 없다' : '은 승급할 수 없다'}`
    : wantsAscend && b.plus < MAX_PLUS ? `재료도 +${MAX_PLUS} 이라야 한다`
    : !wantsAscend && a.plus !== b.plus ? '강화 수치가 다르다'
    : '한쪽만 축복이 붙었다';

  return `<div class="fname"><b>남길 장비 · ${eqName(a)}</b></div>
    <div class="fnote">소모할 장비 · <b>${eqName(b)}</b><br>재료 장비 1개가 사라진다. 결과는 남길 장비에 적용된다.</div>
    <div class="row">${ok
      ? `<button class="gold" onclick="act.${wantsAscend ? 'ascend' : 'merge'}()">`
        + `${wantsAscend ? `승급 — ${gradeOf(gA + 1).name} +0 으로` : `합치기 — +${a.plus + 1} 로`}</button>`
      : `<span class="qt lack">${why}</span>`}${cancel}</div>`;
}

/** 층 전체를 한눈에 — 지금 어디쯤인지 모르면 목표가 안 생긴다 */
function ladder() {
  const rows = [];
  for (let g = 0; g <= MAX_GRADE; g++) {
    const d = gradeOf(g);
    const total = (() => { let n = 1; for (let i = d.safe; i < MAX_PLUS; i++) n += Math.pow(2, i - d.safe); return n; })();
    rows.push(`<span style="color:${gradeCol(g)}">${d.name}</span>
      <span class="qt">${g === MAX_GRADE
        ? `+0 →은화→ +${d.safe} → +${MAX_PLUS} →축복받은 강화석 ${ULTIMATE_BSTONES}개·${ULTIMATE_CHANCE * 100}%→ 제한 없음`
        : `+0 →은화→ +${d.safe} →머지→ +${MAX_PLUS} · 머지 재료 총 ${total}개분`}</span>`);
  }
  // 규칙 문장은 `ascendRule` 하나에서 온다 — 네 자리에 적었더니 넷이 같이 낡았다
  return rows.join('<br>') + `<br><span class="qt">${ascendRule()}</span>`;
}
